import type { components } from "@contracts/generated/editor-service";

import { ApiError } from "@/api/client";

import {
  ASSET_TYPE_LABELS,
  SECTION_BY_ASSET_TYPE,
  type LibraryAssetType,
} from "./librarySections";

/**
 * The clinical-inventory vocabulary: server facts → the words a clinician reads
 * (#1086, consuming #1085's `GET /v1/projects/{id}/clinical-inventory`).
 *
 * ## NOTHING HERE DECIDES ANYTHING
 *
 * The prerequisite's whole design is that provenance, presence, activation and
 * editability are derived server-side from the engine's own supply records —
 * "never value comparison, so a base-equal authored value still reads `draft`".
 * The bounded scope of this ticket repeats that as a prohibition: *do not
 * calculate provenance, inheritance or runtime activation in JavaScript*.
 *
 * So every function below is a TOTAL MAP over a closed contract enum. There is
 * no comparison of two values anywhere in this file, no inference from a field
 * being absent, and no fallback that invents a verdict the service declined to
 * give: each enum's "cannot be established" member gets its own sentence rather
 * than collapsing into the nearest confident one. `satisfies Record<Enum, …>`
 * is what keeps that true as the contract grows — a member added by a
 * regeneration fails to compile here instead of rendering as `undefined`.
 *
 * ## FOUR FACTS, FOUR TABLES
 *
 * The four are orthogonal and the screen must not merge them: an inherited
 * component can be unreachable, a present one can be uneditable, an adopted one
 * can be absent from the tree. One table each, and the row renders them as
 * separate labelled facts.
 */

type Schemas = components["schemas"];

export type ClinicalInventory = Schemas["ClinicalInventory"];
export type ClinicalComponent = Schemas["ClinicalComponent"];
export type ClinicalSetting = Schemas["ClinicalSetting"];
export type ClinicalRole = Schemas["ClinicalRole"];
export type ClinicalSource = Schemas["ClinicalSource"];
export type ClinicalDetail = Schemas["ClinicalDetail"];
export type ClinicalOwnershipBoundary = Schemas["ClinicalOwnershipBoundary"];
export type ClinicalInventoryIdentity = Schemas["ClinicalInventoryIdentity"];
export type ClinicalSupply = Schemas["ClinicalSupply"];
export type ClinicalPresence = Schemas["ClinicalPresence"];
export type ClinicalActivation = Schemas["ClinicalActivation"];
export type ClinicalEditability = Schemas["ClinicalEditability"];
export type ClinicalEditabilityReason = Schemas["ClinicalEditabilityReason"];
export type ClinicalSettingKey = Schemas["ClinicalSettingKey"];
export type ClinicalSettingState = Schemas["ClinicalSettingState"];
export type ClinicalSourceKind = Schemas["ClinicalSourceKind"];
export type ClinicalOwnershipOwner = Schemas["ClinicalOwnershipOwner"];
export type ClinicalOwnershipSubject = Schemas["ClinicalOwnershipSubject"];
export type FamilyAvailability = Schemas["FamilyAvailability"];
export type SectionSlug = Schemas["SectionSlug"];
export type CandidateSupersessionReason =
  Schemas["CandidateSupersessionReason"];
export type IdentityMismatchScope = Schemas["IdentityMismatchScope"];

/** A badge tone, as `ui/Badge` publishes them. */
export type Tone = "neutral" | "success" | "warning" | "accent" | "outline";

export interface FactLabel {
  /** The word on the badge or in the metadata line. Always text, never colour alone. */
  readonly label: string;
  /** One plain sentence a clinician can act on. */
  readonly explanation: string;
}

/**
 * WHICH LAYER SUPPLIED THE CONTENT — the ticket's "origin", and the one fact
 * the surface leads with.
 *
 * `absent` and `unresolved` are deliberately NOT drawn as inheritance. "Nothing
 * supplies it" and "which layer supplies it could not be established" are two
 * different answers, and both differ from "the base supplies it" — the
 * conflation the bounded scope names ("show missing/unresolved content without
 * presenting it as disabled or an empty successful result").
 */
export const ORIGIN_LABELS = {
  base: {
    label: "Inherited",
    explanation:
      "The base pack supplies this content. This project has not changed it.",
  },
  adopted: {
    label: "Adopted",
    explanation:
      "This project pins an exact library version, which supplies this content instead of the base.",
  },
  draft: {
    label: "Draft",
    explanation: "This project's own draft supplies this content.",
  },
  mixed: {
    label: "Mixed",
    explanation:
      "More than one source supplies parts of this content. The sources are listed below.",
  },
  absent: {
    label: "Not supplied",
    explanation: "No source supplies this content to the project.",
  },
  unresolved: {
    label: "Source not established",
    explanation:
      "The Editor could not attribute this content to a source. This is a refusal to guess, not an absence of content.",
  },
} as const satisfies Record<ClinicalSupply, FactLabel>;

/**
 * WHETHER THE MERGED CONTENT TREE HOLDS THE FILES.
 *
 * `present` carries no line of its own: it is the ordinary case, and a row that
 * repeated "present" on every entry would bury the two that are not.
 */
export const PRESENCE_LABELS = {
  present: {
    label: "In this project",
    explanation: "The project's content holds this component's files.",
  },
  absent: {
    label: "Missing",
    explanation:
      "The location is known and nothing is there. Content that should exist here does not.",
  },
  unresolved: {
    label: "Location unresolved",
    explanation:
      "No location could be derived for this component, so whether it is present cannot be decided here.",
  },
} as const satisfies Record<ClinicalPresence, FactLabel>;

/**
 * WHAT THE PINNED RUNTIME WOULD DO WITH IT.
 *
 * Seven members, because the resolver's five verdicts and its two refusals are
 * different facts. `not_applicable` is not "off": it means the family needs no
 * registration and its files are opened unconditionally.
 */
export const ACTIVATION_LABELS = {
  conversation: {
    label: "Runs in the conversation",
    explanation:
      "The pinned engine reaches this content during a patient conversation.",
  },
  summary_only: {
    label: "Runs for the summary only",
    explanation:
      "The pinned engine reaches this content when the summary is written, not during the conversation.",
  },
  no_registry: {
    label: "Not registered in the flow",
    explanation:
      "Nothing in this project's flow registers this content, so the engine never opens it.",
  },
  unreachable: {
    label: "Registered but not reachable",
    explanation:
      "This project's flow registers content the engine cannot reach from where a conversation starts.",
  },
  engine_unsupported: {
    label: "Unsupported by the pinned engine",
    explanation:
      "The pinned engine has no support for this kind of content. Its files are present and unused.",
  },
  not_applicable: {
    label: "No flow registration needed",
    explanation:
      "This family needs no flow registration — the engine opens whatever files it has unconditionally.",
  },
  unresolved: {
    label: "Runtime use not established",
    explanation:
      "No authority here holds what is needed to decide whether the engine reaches this content.",
  },
} as const satisfies Record<ClinicalActivation, FactLabel>;

/** WHETHER A CLINICIAN MAY AUTHOR IT HERE. Never merged with activation. */
export const EDITABILITY_LABELS = {
  editable: {
    label: "Editable here",
    explanation: "This project can author its own version of this content.",
  },
  protected: {
    label: "Protected — not editable here",
    explanation: "This content exists and cannot be authored in the Editor.",
  },
  unsupported: {
    label: "No authoring surface here",
    explanation:
      "The Editor publishes no authoring surface for this kind of content.",
  },
} as const satisfies Record<ClinicalEditability, FactLabel>;

/** WHY it is not plainly editable — the server's own reason, never guessed. */
export const EDITABILITY_REASON_LABELS = {
  safety_floor:
    "Locked by the safety floor, which every project runs unchanged.",
  governed_action:
    "Changed only through its own named control, not by editing content.",
  external_source: "Approved outside the Editor and adopted, never authored.",
  family_unavailable:
    "The placement registry does not support placing this family.",
  placement_unresolved:
    "Its location could not be derived, so no authoring surface can be offered.",
} as const satisfies Record<ClinicalEditabilityReason, string>;

/** The family's published availability. Shown only when it is not `supported`. */
export const AVAILABILITY_LABELS = {
  supported: "Supported",
  protected: "Protected family",
  unavailable: "Unavailable family",
} as const satisfies Record<FamilyAvailability, string>;

/** Which layer one source is. */
export const SOURCE_KIND_LABELS = {
  base_pack: "Base pack",
  library_adoption: "Library adoption",
  project_draft: "Project draft",
} as const satisfies Record<ClinicalSourceKind, string>;

/**
 * THE TYPED PROJECT DECISIONS, named as the product names them.
 *
 * Keyed off the contract's own closed enum rather than off prose, so a new
 * setting cannot appear on the screen as a bare snake-case token.
 */
export const SETTING_LABELS = {
  final_questioning: {
    label: "Final safety questioning",
    explanation:
      "Whether a session ends with the governed final safety questions.",
  },
  summary_generation_policy: {
    label: "Summary generation",
    explanation: "Which secondary indicators the clinical summary generates.",
  },
  response_filtering: {
    label: "Response filtering",
    explanation: "Which Output Guard signals this project acts on.",
  },
  interview_variant: {
    label: "Background interview profile",
    explanation:
      "Which background-interview variant a session asks before the clinical interview.",
  },
  flow_registration: {
    label: "Flow registration",
    explanation:
      "The dynamic-pathway slots, track priority and session languages the flow registers.",
  },
} as const satisfies Record<ClinicalSettingKey, FactLabel>;

/**
 * A setting's own state. `disabled` is a DECISION, not a missing value, and
 * `unresolved` is a failed read — the three states the bounded scope asks to
 * keep distinct from one another.
 */
export const SETTING_STATE_LABELS = {
  inherited: {
    label: "Inherited",
    explanation: "This project has decided nothing; the base supplies it.",
  },
  authored: {
    label: "Authored",
    explanation: "This project holds its own decision.",
  },
  disabled: {
    label: "Turned off",
    explanation: "This project's decision is to turn the behaviour off.",
  },
  unresolved: {
    label: "Not readable",
    explanation: "This project's decision could not be read.",
  },
} as const satisfies Record<ClinicalSettingState, FactLabel>;

/** Who owns a named area of runtime configuration, when the Editor does not. */
export const BOUNDARY_OWNER_LABELS = {
  tenant_manifest: "Tenant Manifest",
  runtime_target: "The service that runs the conversation",
} as const satisfies Record<ClinicalOwnershipOwner, string>;

export const BOUNDARY_SUBJECT_LABELS = {
  speech_provider_model: "Speech provider and model",
  speech_credentials: "Speech credentials",
  runtime_detector_policy: "Runtime safety detection policy",
} as const satisfies Record<ClinicalOwnershipSubject, string>;

/**
 * Why a frozen test version is no longer the project's current configuration.
 *
 * The server derives supersession; this only spells its reasons. Each names one
 * of the identity coordinates the inventory response is itself bound to, which
 * is what lets the two blocks on this screen be read together.
 */
export const SUPERSESSION_REASON_LABELS = {
  newer_candidate: "a newer test version was frozen since",
  draft_changed: "the draft changed",
  adoptions_changed: "the adopted library versions changed",
  governed_settings_changed: "a governed setting changed",
  base_changed: "the base pack changed",
  final_questioning_changed: "the final safety questioning decision changed",
} as const satisfies Record<CandidateSupersessionReason, string>;

/**
 * What to call a component on screen.
 *
 * THE KEY IS SOMETIMES THE ONLY IDENTIFIER, and the contract says so outright:
 * a legacy flat background domain read against a catalog that does not declare
 * it carries a null `name` (the catalog supplies names) AND a null `coordinate`
 * (the shape has none), "so the domain id must be read from here". Reading it
 * off the key is therefore the contract's own instruction rather than this
 * surface inventing an identity.
 *
 * The order is name → coordinate → key, and the last step splits on the FIRST
 * colon because both keyed shapes are `<family>:<rest>`; a key with no colon IS
 * the family, so the family's product name is what a clinician should see.
 *
 * An empty string is treated as unnamed. The contract types `name` as
 * `string | null`, and a row titled with nothing at all is the one outcome this
 * screen must never produce — every inherited component is reported, "under its
 * coordinate", precisely so it cannot go missing.
 */
export function componentTitle(component: ClinicalComponent): string {
  if (component.name !== null && component.name !== undefined && component.name !== "") {
    return component.name;
  }
  if (component.coordinate !== null && component.coordinate !== undefined && component.coordinate !== "") {
    return component.coordinate;
  }
  const separator = component.key.indexOf(":");
  return separator === -1
    ? (ASSET_TYPE_LABELS[component.family] ?? component.key)
    : component.key.slice(separator + 1);
}

/** True when the catalog could not name this component — stated, not hidden. */
export function isUnnamed(component: ClinicalComponent): boolean {
  return (
    component.name === null ||
    component.name === undefined ||
    component.name === ""
  );
}

/**
 * The name each row's Details control announces itself with, keyed by
 * component.
 *
 * `CatalogDetails` records why this matters: "a page of buttons all named
 * `Details`" is a repeated-ambiguous-control defect, so each carries its
 * subject. A TITLE is not guaranteed unique — the catalog may name two
 * components the same thing, and the unnamed ones fall back to a coordinate —
 * while the `key` is unique within the response by construction. So a title
 * that appears once is used as-is, and one that repeats is qualified by the key
 * that distinguishes it. Computed over the whole list rather than per row,
 * because "is this name ambiguous" is a question about the page.
 */
export function componentSubjects(
  components: readonly ClinicalComponent[],
): Map<string, string> {
  const counts = new Map<string, number>();
  for (const component of components) {
    const title = componentTitle(component);
    counts.set(title, (counts.get(title) ?? 0) + 1);
  }
  return new Map(
    components.map((component) => {
      const title = componentTitle(component);
      return [
        component.key,
        (counts.get(title) ?? 0) > 1 ? `${title} (${component.key})` : title,
      ];
    }),
  );
}

/**
 * One source, as a sentence naming its EXACT identity.
 *
 * `version` is nullable "only where the coordinate genuinely has none", so an
 * absent version says so rather than being silently dropped — an adopted pin
 * whose version cannot be named is exactly the case where the detail link is
 * also withheld, and the two must agree.
 */
export function sourceSummary(source: ClinicalSource): string {
  const kind = SOURCE_KIND_LABELS[source.kind];
  const name = source.name ?? source.ref;
  const version =
    source.version === null || source.version === undefined
      ? "no version recorded"
      : source.version;
  return `${kind}: ${name} (${version})`;
}

/**
 * One source at its exact identity, short enough for a compact row.
 *
 * `sourceSummary` above is the Details spelling — it names the layer in words.
 * This is the row spelling: the thing a clinician scans for is WHICH VERSION,
 * and the layer is already carried by the origin badge beside it.
 */
export function sourceShortLabel(source: ClinicalSource): string {
  const version =
    source.version === null || source.version === undefined
      ? "no version recorded"
      : source.version;
  const name =
    source.kind === "base_pack" ? source.ref : (source.name ?? source.ref);

  // A LAYER THAT ONLY REMOVES IS NOT A VERSION THIS COMPONENT RUNS. The service
  // folds tombstones and any unattributed adoption file into one roll-up row
  // whose "version" is the pin-set token, so `adoptions 3` would read as a
  // version 3 of something called adoptions. Keyed on the COUNTS the row
  // carries rather than on its ref, so nothing here depends on a magic string.
  if (source.suppliedPaths === 0 && source.tombstonedPaths > 0) {
    return `${name} (removes ${source.tombstonedPaths})`;
  }
  if (source.kind === "project_draft") {
    return `draft ${version}`;
  }
  return `${name} ${version}`;
}

/**
 * The identity of the layer(s) that actually supply this component — the row's
 * "source/version" fact.
 *
 * ## WHY THIS IS SELECTED BY `supply` AND NOT BY WHAT HAPPENS TO BE PRESENT
 *
 * Two fields make the obvious reading wrong, and both are DELIBERATE on the
 * service side:
 *
 *  * **the base row appears even when every base file is overridden.** The
 *    service says so in as many words: those rows state which layers HOLD this
 *    component's files, and `supply` states who supplies the effective content.
 *    Picking "the base source, if there is one" therefore prints
 *    `From default 2026.09.1` beside a `Draft` badge.
 *  * **`adoptedVersion` is the project's PIN, independent of `supply`.** A
 *    draft that overrides an adopted asset leaves it populated, so keying on it
 *    prints `Pinned v1` for content the draft supplies — hiding the version
 *    that is actually running.
 *
 * So the layer named by `supply` decides which sources are quoted, and `mixed`
 * quotes EVERY contributor in the server's order rather than picking one. A
 * layer with no matching source row is quoted as nothing at all rather than as
 * some other layer's version; `Details` still lists every source either way.
 */
export function effectiveSourceLabels(
  component: ClinicalComponent,
): string[] {
  const of = (kind: ClinicalSourceKind) =>
    component.sources
      .filter((source) => source.kind === kind)
      .map(sourceShortLabel);

  switch (component.supply) {
    case "base":
      return of("base_pack");
    case "adopted":
      return of("library_adoption");
    case "draft":
      return of("project_draft");
    case "mixed":
      return component.sources.map(sourceShortLabel);
    case "absent":
    case "unresolved":
      return [];
  }
}

/**
 * "library head vN", when the project's pin is behind it.
 *
 * The server's own two labels, never a comparison of content, and only for a
 * component whose effective content really is the adopted layer — a pin that no
 * longer supplies anything is not "outdated", it is overridden, and saying both
 * at once would be two claims where the row has room for one.
 */
export function libraryHeadNote(component: ClinicalComponent): string | null {
  if (component.supply !== "adopted" && component.supply !== "mixed") {
    return null;
  }
  const { adoptedVersion, latestVersion } = component;
  if (
    adoptedVersion === null ||
    adoptedVersion === undefined ||
    latestVersion === null ||
    latestVersion === undefined ||
    latestVersion === adoptedVersion
  ) {
    return null;
  }
  return `library head ${latestVersion}`;
}

/**
 * How many of a component's placed files one layer supplied, and how many it
 * deleted.
 *
 * "Zero supplied with a non-zero tombstone count is a layer that only removed
 * content" is the contract's own reading, and it is the one case a bare file
 * count would misreport as "this layer contributes nothing".
 */
export function sourcePathSummary(source: ClinicalSource): string {
  if (source.suppliedPaths === 0 && source.tombstonedPaths > 0) {
    return `removes ${source.tombstonedPaths} file(s), supplies none`;
  }
  if (source.tombstonedPaths > 0) {
    return `supplies ${source.suppliedPaths} file(s), removes ${source.tombstonedPaths}`;
  }
  return `supplies ${source.suppliedPaths} file(s)`;
}

/**
 * Where a clinician goes to read or change one component's values — an EXISTING
 * named route, or nothing.
 *
 * The bounded scope allows exactly this and no more: "connect valid API detail
 * targets to existing named UI routes … do not invent unsupported editing
 * paths". So the only inputs are `detail.kind` and `detail.path`, both
 * server-owned, and every arm either resolves to a route this application
 * actually mounts or returns `null`.
 *
 * `kind: "none"` is a REAL ANSWER, not a gap — the contract is explicit that a
 * field meaning "where do these values live" is worse than useless when it
 * names a read that returns something else. A Pathway has no such read, and
 * neither do the families whose relpaths no section claims. The surface states
 * that rather than offering a link to somewhere the values are not.
 *
 * A path that does not parse also returns `null`. That is the fail-closed
 * direction: a malformed or future-shaped path must not become a link to a
 * guessed address.
 */
/** A destination inside the Project's own clinical-configuration rail. */
export interface SectionDetailTarget {
  readonly kind: "section";
  readonly href: string;
  readonly section: string;
}

export type DetailTarget =
  | SectionDetailTarget
  | {
      readonly kind: "library-asset";
      readonly href: string;
      readonly assetId: string;
      /** The exact immutable version the href is known to serve. */
      readonly version: string;
    }
  | {
      /**
       * This project runs an exact library version that no existing page
       * serves. Not a gap in the response — a gap in this application, stated.
       */
      readonly kind: "pinned-version";
      readonly assetId: string;
      readonly version: string;
      /** The library head, when the server reported one. */
      readonly head: string | null;
    };

const SECTION_DETAIL = /^\/v1\/projects\/[^/]+\/sections\/([^/?#]+)$/;
const LIBRARY_VERSION_DETAIL =
  /^\/v1\/library\/assets\/([^/?#]+)\/versions\/([^/?#]+)$/;

/**
 * The clinical-configuration rail slugs an inventory `section` can address.
 *
 * Declared against the contract's own `SectionSlug` union so the two cannot
 * drift: every member is a real `/projects/:id/config/:section` destination.
 */
const SECTION_ROUTE_SLUGS = [
  "bg-sets",
  "chief-complaint",
  "pathways",
  "rules",
  "safety",
  "alerts",
  "summary",
] as const satisfies readonly SectionSlug[];

function isSectionRouteSlug(value: string): value is SectionSlug {
  return (SECTION_ROUTE_SLUGS as readonly string[]).includes(value);
}

export function componentDetailTarget(
  projectId: string,
  component: ClinicalComponent,
): DetailTarget | null {
  const { detail, family } = component;
  const path = detail.path ?? "";

  if (detail.kind === "project_section") {
    const section = SECTION_DETAIL.exec(path)?.[1];
    // The section is read from the PATH, never from `component.section`: the
    // contract says the two "deliberately disagree" for the families whose
    // files no section serves, and `section` is the grouping while this is the
    // read that owns the values.
    if (section === undefined || !isSectionRouteSlug(section)) {
      return null;
    }
    return {
      kind: "section",
      section,
      href: `/projects/${encodeURIComponent(projectId)}/config/${section}`,
    };
  }

  if (detail.kind === "library_asset") {
    const match = LIBRARY_VERSION_DETAIL.exec(path);
    if (match === null) {
      return null;
    }
    const assetId = decodeURIComponent(match[1] as string);
    const version = decodeURIComponent(match[2] as string);
    // `libraryAssetHref`'s derivation, from the component's own declared
    // family: the asset page is addressed by the library rail's section, which
    // the API path does not carry.
    const librarySection = SECTION_BY_ASSET_TYPE[family as LibraryAssetType];
    if (librarySection === undefined) {
      return null;
    }

    // THE VERSION IN THE PATH IS THE WHOLE POINT OF THE PATH, and dropping it
    // is the defect this arm exists to avoid. The contract says outright that
    // `library_asset` "is the EXACT PINNED IMMUTABLE VERSION, never the asset's
    // current head — a Project on v2 while the library head is v3 must not be
    // sent to content it does not run", and `/library/{section}/{assetId}` is
    // exactly that current-head page.
    //
    // So the link is offered only when the head IS the pinned version, which
    // is when that page serves the bytes this project runs. `latestVersion` is
    // the server's own reading of the head; comparing two server-supplied
    // version LABELS is a routing decision, not a provenance one, and it is the
    // same comparison `lib/adoptions.ts` already makes for the same purpose.
    // An absent head establishes nothing, so it withholds — fail closed.
    const head = component.latestVersion ?? null;
    if (head !== version) {
      return { kind: "pinned-version", assetId, version, head };
    }

    return {
      kind: "library-asset",
      assetId,
      version,
      href: `/library/${librarySection}/${encodeURIComponent(assetId)}`,
    };
  }

  // `typed_setting` never reaches a component today — the contract gives it to
  // the settings block — and `none` is the answer that says no read owns these
  // values. Both are `null` here rather than a link this surface cannot honour.
  return null;
}

/**
 * Where each typed setting is decided, by its own closed key.
 *
 * Keyed off `ClinicalSettingKey` rather than parsed out of `detail.path`,
 * because these are CONTROLS rather than content reads: the API path names the
 * endpoint that serves the decision, and what a clinician needs is the screen
 * whose named control makes it. Exhaustive by `satisfies`, so a sixth setting
 * cannot ship with no destination.
 */
export const SETTING_SECTION = {
  final_questioning: "safety",
  summary_generation_policy: "summary",
  response_filtering: "rules",
  interview_variant: "bg-sets",
  flow_registration: "pathways",
} as const satisfies Record<ClinicalSettingKey, SectionSlug>;

export function settingDetailTarget(
  projectId: string,
  setting: ClinicalSetting,
): SectionDetailTarget | null {
  if (setting.detail.kind !== "typed_setting") {
    return null;
  }
  const section = SETTING_SECTION[setting.key];
  return {
    kind: "section",
    section,
    href: `/projects/${encodeURIComponent(projectId)}/config/${section}`,
  };
}

/**
 * The components, grouped the way the rail groups the product.
 *
 * ## THE SERVER'S ORDER SURVIVES INSIDE EACH GROUP
 *
 * The response "stays sorted, so a truncated response is a stable prefix rather
 * than an arbitrary subset". Re-sorting here would throw that away: a truncated
 * response re-ordered in the browser is no longer a prefix of anything, and two
 * reads of the same configuration could disagree about position. So this
 * partitions and never sorts.
 *
 * ## THE PARTITION IS TOTAL, WHICH IS THE WHOLE POINT
 *
 * `section` is nullable by contract — a component whose roles are claimed by
 * more than one section, or by none, reports null rather than picking a winner,
 * and on the real default pack `closing_conversation` is exactly that. Dropping
 * those is precisely the failure this ticket exists to fix ("do not hide
 * inherited components because no library row or detail editor exists").
 *
 * So the last group takes EVERY remaining component — a null section and an
 * unrecognised one alike. The second case matters because a regenerated
 * contract could add an eighth `SectionSlug`, and a partition that matched only
 * the seven known ones would silently drop its components from the one screen
 * whose purpose is that nothing is silently dropped. Landing in this group is
 * not a mislabel either: such a component genuinely is not shown in any
 * configuration section this build renders.
 */
export interface ComponentGroup {
  readonly section: SectionSlug | null;
  readonly label: string;
  readonly components: readonly ClinicalComponent[];
}

const SECTION_GROUP_LABELS = {
  "bg-sets": "BG sets",
  "chief-complaint": "Main concern",
  pathways: "Pathways",
  rules: "Rules",
  safety: "Safety",
  alerts: "Alerts",
  summary: "Clinical summary content",
} as const satisfies Record<SectionSlug, string>;

/**
 * The order the clinical-configuration rail lists its sections in, so the
 * inventory reads down the screen in the same order a clinician navigates.
 */
const SECTION_GROUP_ORDER = [
  "bg-sets",
  "chief-complaint",
  "pathways",
  "rules",
  "safety",
  "alerts",
  "summary",
] as const satisfies readonly SectionSlug[];

export const UNSECTIONED_GROUP_LABEL = "Not shown in a configuration section";

export function groupComponentsBySection(
  components: readonly ClinicalComponent[],
): ComponentGroup[] {
  const groups: ComponentGroup[] = [];

  for (const section of SECTION_GROUP_ORDER) {
    const members = components.filter(
      (component) => component.section === section,
    );
    if (members.length > 0) {
      groups.push({
        section,
        label: SECTION_GROUP_LABELS[section],
        components: members,
      });
    }
  }

  const remaining = components.filter(
    (component) =>
      component.section === null ||
      component.section === undefined ||
      !isSectionRouteSlug(component.section),
  );
  if (remaining.length > 0) {
    groups.push({
      section: null,
      label: UNSECTIONED_GROUP_LABEL,
      components: remaining,
    });
  }

  return groups;
}

/**
 * How many components the grouping placed.
 *
 * The surface asserts this against the response's own length: a partition that
 * lost a row would be the defect this screen exists to prevent, and a count is
 * how it becomes visible rather than invisible.
 */
export function groupedComponentCount(groups: readonly ComponentGroup[]): number {
  return groups.reduce((total, group) => total + group.components.length, 0);
}

/**
 * The row's metadata line: the facts that are NOT the ordinary case.
 *
 * `present`, `not_applicable` activation and `editable` are the unremarkable
 * readings; a line repeating them on all 38 rows would hide the two rows where
 * something is wrong. Each entry returned here is a distinct string so the row
 * can render one element per fact — `CatalogMeta`'s rule, so a screen reader
 * announces four facts rather than one run-on sentence.
 */
export function componentFacts(component: ClinicalComponent): string[] {
  const facts: string[] = [];

  if (component.presence !== "present") {
    facts.push(PRESENCE_LABELS[component.presence].label);
  }
  if (component.availability !== "supported") {
    facts.push(AVAILABILITY_LABELS[component.availability]);
  }
  facts.push(ACTIVATION_LABELS[component.activation].label);
  if (component.editability !== "editable") {
    facts.push(EDITABILITY_LABELS[component.editability].label);
  }

  return facts;
}

/**
 * The badge tone for an origin.
 *
 * Tone is decoration over the label, never the carrier of the fact: every
 * caller renders `ORIGIN_LABELS[supply].label` beside it, which is what makes
 * the four origins distinguishable without colour.
 */
export const ORIGIN_TONES = {
  base: "outline",
  adopted: "accent",
  draft: "accent",
  mixed: "warning",
  absent: "warning",
  unresolved: "warning",
} as const satisfies Record<ClinicalSupply, Tone>;

export const SETTING_STATE_TONES = {
  inherited: "outline",
  authored: "accent",
  disabled: "neutral",
  unresolved: "warning",
} as const satisfies Record<ClinicalSettingState, Tone>;

/**
 * Why a read of the inventory failed, in the terms the surface must SAY.
 *
 * The route declares SIX distinguishable refusals and they call for different
 * sentences — and, decisively, for different ANSWERS to "will trying again
 * help". That is the bounded scope's "handle stale/conflicting/failed reads
 * explicitly".
 *
 * ## THE THREE 409s ARE NOT ONE 409
 *
 * `ApiError.isConflict` is true for all of them, and treating the status alone
 * as the concurrent-move case tells a clinician "nothing is wrong with the
 * project, read it again" about two states where something IS wrong and no
 * number of re-reads will clear it. The route's own `responses` entry names all
 * three, and the service's error envelope carries what tells them apart:
 *
 *  * `projection_identity_mismatch` — a stored pin disagrees with the runtime
 *    (`details.mismatch` is `project_base` or `draft_base`). The service
 *    refuses BEFORE composing anything, deliberately: an inventory of content
 *    it cannot verify is worse than no inventory. PERSISTENT — the recovery is
 *    to re-resolve the project's base, not to retry.
 *  * `conflict` WITH `error.field` — the stored draft/adoption state cannot be
 *    composed at all, and the field names the first offending address or asset
 *    (a path, never content). PERSISTENT — the stored state has to change.
 *  * `conflict` with NO field — the configuration moved while the response was
 *    being composed. TRANSIENT: the identical request is the whole recovery,
 *    and the service's own test asserts the next read settles.
 *
 * `field` and `details` are read off the envelope rather than matched in the
 * message, which is what those fields exist for.
 */
export type InventoryReadFailure =
  /** A pin, the draft, a setting or the posture moved mid-composition. Retry. */
  | { kind: "moved"; requestId: string | undefined }
  /** A stored pin disagrees with the runtime. Retrying cannot change that. */
  | {
      kind: "identity_mismatch";
      mismatch: IdentityMismatchScope | null;
      requestId: string | undefined;
    }
  /** The stored draft/adoption state cannot be composed. Not retryable. */
  | { kind: "uncomposable"; field: string | null; requestId: string | undefined }
  | { kind: "unavailable"; requestId: string | undefined }
  | { kind: "denied" }
  | { kind: "error"; code: string; requestId: string | undefined };

function envelopeString(error: ApiError, key: string): string | null {
  const value = error.envelope[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * `details.mismatch`, when the envelope carries the typed payload.
 *
 * `ApiError.details` is `unknown` at the transport layer on purpose — that
 * module must not grow a domain shape — so each domain narrows it here, as
 * `adoptionRefusalFromError` does. An unrecognised shape reads as `null` rather
 * than as a guessed scope: the surface then says which project it is about
 * without claiming which pin drifted.
 */
function mismatchScope(error: ApiError): IdentityMismatchScope | null {
  const details = error.details;
  if (typeof details !== "object" || details === null) {
    return null;
  }
  const mismatch = (details as { mismatch?: unknown }).mismatch;
  return mismatch === "project_base" || mismatch === "draft_base"
    ? mismatch
    : null;
}

export function classifyInventoryReadFailure(
  error: unknown,
): InventoryReadFailure {
  if (!(error instanceof ApiError)) {
    return { kind: "error", code: "transport", requestId: undefined };
  }
  if (error.code === "projection_identity_mismatch") {
    return {
      kind: "identity_mismatch",
      mismatch: mismatchScope(error),
      requestId: error.requestId,
    };
  }
  if (error.isConflict) {
    const field = envelopeString(error, "field");
    // A field POINTS AT the stored state that cannot be composed. Its absence
    // is the concurrent-move case — the only one of the three a retry clears.
    return field === null
      ? { kind: "moved", requestId: error.requestId }
      : { kind: "uncomposable", field, requestId: error.requestId };
  }
  if (error.status === 503 || error.status === 502 || error.status === 504) {
    return { kind: "unavailable", requestId: error.requestId };
  }
  if (error.isForbidden || error.isUnauthenticated || error.status === 404) {
    return { kind: "denied" };
  }
  return { kind: "error", code: error.code, requestId: error.requestId };
}
